import React, { useState } from "react";
import { ComponentConfig, DropZone, usePuck } from "@measured/puck";
import Wrapper from "../../../utils/Wrapper";
import { BsTrophyFill } from "react-icons/bs";
import { Tooltip } from "../../../utils/Tooltip";
import { EditableText } from "../../../utils/components";
import { useWebsiteContext } from "../../../../context/website";
import { ImageField } from "../../../puck_fields";
import { type globalCommonFieldsType, createDefaultProps, globalCommonFields, type textFieldsType, textFields } from "../../../utils/CommonFields";
import { generateSpacingCSS, generateBorderCSS, GetCurrentFont } from '../../../helpers'
import { themes } from "../../../../theme";

export type PricingProps = globalCommonFieldsType & {
    title: {
        text: string;
        typography: textFieldsType
    };
    description: {
        text: string;
        typography: textFieldsType
    };
    services: {
        label: {
            text: string;
            typography: textFieldsType
        };
        description: {
            text: string;
            typography: textFieldsType
        };
        price: {
            text: string;
            typography: textFieldsType
        };
        url: string;
    }[];
    backgroundImages: {
        url_1: string,
        url_2: string,
    }
};

export const Pricing: ComponentConfig<PricingProps> = {
    fields: {
        title: {
            type: "object",
            objectFields: {
                text: { type: "textarea", label: "Text" },
                typography: {
                    type: "object",
                    objectFields: textFields,
                    label: "Typography"
                },
            },
            label: "Title"
        },
        description: {
            type: "object",
            objectFields: {
                text: { type: "textarea", label: "Text" },
                typography: {
                    type: "object",
                    objectFields: textFields,
                    label: "Typography"
                },
            },
            label: "Description"
        },
        services: {
            type: "array",
            getItemSummary: (item, i) => item.label.text || `Service #${i + 1}`,
            arrayFields: {
                label: {
                    type: "object",
                    objectFields: {
                        text: { type: "textarea", label: "Text" },
                        typography: {
                            type: "object",
                            objectFields: textFields,
                            label: "Typography"
                        },
                    },
                    label: "Title"
                },
                description: {
                    type: "object",
                    objectFields: {
                        text: { type: "textarea", label: "Text" },
                        typography: {
                            type: "object",
                            objectFields: textFields,
                            label: "Typography"
                        },
                    },
                    label: "Description"
                },
                url: { ...ImageField, label: "Url" },
                price: {
                    type: "object",
                    objectFields: {
                        text: { type: "textarea", label: "Text" },
                        typography: {
                            type: "object",
                            objectFields: textFields,
                            label: "Typography"
                        },
                    },
                    label: "Price"
                },
            },
            defaultItemProps: {
                url: "https://media.istockphoto.com/id/1367037346/video/4k-video-footage-of-a-young-woman-enjoying-a-relaxing-facial-at-a-spa.jpg?s=640x640&k=20&c=ah61W-9YfaP7VXg3WeRaR991aaOokTIpzzP7o7GExHc=",
                label: {
                    text: "Massaging",
                    typography: createDefaultProps("text", {
                        fontSize: 20,
                        fontWeight: 600,
                        lineHeight: 1.3,
                        font: { name: "Roboto" }
                    }),
                },
                description: {
                    text: "It’s a 45 minutes service",
                    typography: createDefaultProps("text", {
                        font: { name: "Roboto" },
                        fontSize: 15,
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: { hex: themes.theme_1.colors.neutral }
                    }),
                },
                price: {
                    text: "$75",
                    typography: createDefaultProps("text", {
                        fontSize: 20,
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: { hex: themes.theme_1.colors.neutral }
                    }),
                },
            },
            label: "Services"
        },
        backgroundImages: {
            type: "object",
            objectFields: {
                url_1: { ...ImageField, label: "Url 1" },
                url_2: { ...ImageField, label: "Url 2" },
            },
            label: "Images"
        },
        ...globalCommonFields
    },
    defaultProps: {
        title: {
            text: "It'S NOT YOU, IT'S YOUR EYEBROWS",
            typography: createDefaultProps("text", {
                font: { name: "Roboto" },
                fontSize: 15,
                fontWeight: 600,
                lineHeight: 1.05,
            }),
        },
        description: {
            text: "For that feminine look you have always wanted.",
            typography: createDefaultProps("text", {
                fontSize: 35,
                fontWeight: 500,
                lineHeight: 1.3,
            }),
        },
        services: [
            {
                url: "https://media.istockphoto.com/id/1367037346/video/4k-video-footage-of-a-young-woman-enjoying-a-relaxing-facial-at-a-spa.jpg?s=640x640&k=20&c=ah61W-9YfaP7VXg3WeRaR991aaOokTIpzzP7o7GExHc=",
                label: {
                    text: "Cleansing and Massaging",
                    typography: createDefaultProps("text", {
                        fontSize: 20,
                        fontWeight: 600,
                        lineHeight: 1.3,
                        font: { name: "Roboto" }
                    }),
                },
                description: {
                    text: "It’s a 45 minutes service",
                    typography: createDefaultProps("text", {
                        font: { name: "Roboto" },
                        fontSize: 15,
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: { hex: themes.theme_1.colors.neutral }
                    }),
                },
                price: {
                    text: "$75",
                    typography: createDefaultProps("text", {
                        fontSize: 20,
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: { hex: themes.theme_1.colors.neutral }
                    }),
                },
            },
            {
                url: "https://static.vecteezy.com/system/resources/previews/004/675/721/non_2x/middle-aged-woman-having-a-head-massage-in-a-beauty-salon-photo.jpg",
                label: {
                    text: "Threading",
                    typography: createDefaultProps("text", {
                        fontWeight: 600,
                        lineHeight: 1.3,
                        font: { name: "Roboto" }
                    }),
                },
                description: {
                    text: "It’s a 45 minutes service",
                    typography: createDefaultProps("text", {
                        font: { name: "Roboto" },
                        fontSize: 15,
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: { hex: themes.theme_1.colors.neutral }
                    }),
                },
                price: {
                    text: "$100",
                    typography: createDefaultProps("text", {
                        fontSize: 20,
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: { hex: themes.theme_1.colors.neutral }
                    }),
                },
            },
            {
                url: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTaNOqVWA3K-nJXKi9XfncBfxI-WIk7HwsaIZutHHAn9Q&s",
                label: {
                    text: "Hairdressing",
                    typography: createDefaultProps("text", {
                        fontWeight: 600,
                        lineHeight: 1.3,
                        font: { name: "Roboto" }
                    }),
                },
                description: {
                    text: "It’s an hour service",
                    typography: createDefaultProps("text", {
                        font: { name: "Roboto" },
                        fontSize: 15,
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: { hex: themes.theme_1.colors.neutral }
                    }),
                },
                price: {
                    text: "$120",
                    typography: createDefaultProps("text", {
                        fontSize: 20,
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: { hex: themes.theme_1.colors.neutral }
                    }),
                },
            },
        ],
        backgroundImages: {
            url_1: "https://cdn.pixabay.com/photo/2017/11/03/11/33/rose-2914093_1280.png",
            url_2: "https://cdn.pixabay.com/photo/2017/11/26/16/22/rose-2979256_1280.png"
        },
        ...createDefaultProps("globalCommon", { spacing: { padding: { left: 0, top: 100, right: 0, bottom: 100, } } })
    },

    render: (data) => {
        const { websiteTheme } = useWebsiteContext();

        const handleChange = (e, type, identifier = null) => {
            const updatedData = { ...data };
            if (type === 'title') {
                updatedData.title.text = e.target.textContent;
            } else if (type === 'description') {
                updatedData.description.text = e.target.textContent;
            } else if (type == "serviceTitle") {
                updatedData.services[identifier].label.text = e.target.textContent;
            } else if (type == "serviceDescription") {
                updatedData.services[identifier].description.text = e.target.textContent;
            } else if (type == "servicePrice") {
                updatedData.services[identifier].price.text = e.target.textContent;
            }

            return { updatedData: updatedData, type: "Pricing_theme_1_variant_1" }
        };

        const theme = websiteTheme.name === "theme_1" ? websiteTheme.content : themes.theme_1
        const backgroundColor = data.backgroundColor.hex === "Default" ? theme.colors.primary : data.backgroundColor.hex

        return (
            <Wrapper className="tw-relative" styles={{ backgroundColor: backgroundColor, ...generateBorderCSS(data.border), color: theme.colors.text }}>
                <div style={{ padding: generateSpacingCSS(data.spacing.padding), margin: generateSpacingCSS(data.spacing.margin) }} className="tw-relative tw-z-10">
                    <div className={`tw-flex tw-flex-col tw-z-10 tw-gap-4 tw-text-center`}>
                        <DropZone zone="pricing_theme_1_variant_1_1" allow={["Text", "Image", "Link", "Video"]} />

                        <EditableText handleChange={(e) => handleChange(e, "title")} data={data.title.typography}>
                            {data.title.text}
                        </EditableText>
                        <EditableText handleChange={(e) => handleChange(e, "description")} data={data.description.typography}>
                            {data.description.text}
                        </EditableText>


                        <DropZone zone="pricing_theme_1_variant_1_2" allow={["Text", "Image", "Link", "Video"]} />
                    </div>

                    <div className="tw-grid lg:tw-grid-cols-2 tw-gap-16 tw-w-fit tw-mt-16 tw-mx-auto">
                        {data.services.map((service, i) =>
                            <div key={i} className="tw-flex tw-flex-col md:tw-flex-row tw-gap-4">
                                <div className="tw-h-[160px] tw-w-[200px] md:tw-h-[110px] md:tw-w-[120px] tw-relative">
                                    <img
                                        src={service.url}
                                        alt=""
                                        className="tw-w-full tw-h-full tw-object-cover"
                                    />
                                </div>
                                <div className="tw-mt-1 tw-flex tw-flex-col tw-gap-4">
                                    <div className="tw-flex tw-items-center tw-w-[200px] md:tw-w-[320px] xl:tw-w-[500px]">
                                        <EditableText handleChange={(e) => handleChange(e, "serviceTitle", i)} data={service.label.typography}>
                                            {`${service.label.text}`}
                                        </EditableText>
                                        <div className="tw-border-black tw-border-b-2 tw-border-dotted tw-w-full">
                                        </div>
                                        <EditableText handleChange={(e) => handleChange(e, "servicePrice", i)} data={service.price.typography}>
                                            {`${service.price.text}`}
                                        </EditableText>
                                    </div>
                                    <EditableText handleChange={(e) => handleChange(e, "serviceDescription", i)} data={service.description.typography}>
                                        {service.description.text}
                                    </EditableText>
                                </div>
                            </div>
                        )}
                    </div>

                </div>

                <div className="tw-w-[180px] tw-h-[160px] tw-absolute tw-top-[20px] tw-left-0 tw-hidden md:tw-block tw-z-[5]">
                    <img
                        src={data.backgroundImages.url_1}
                        alt=""
                        className="tw-w-full tw-h-full tw-object-contain"
                    />
                </div>

                <div className="tw-w-[180px] tw-h-[160px] tw-absolute tw-top-0 tw-right-0 tw-z-[5]">
                    <img
                        src={data.backgroundImages.url_2}
                        alt=""
                        className="tw-w-full tw-h-full tw-object-contain"
                    />
                </div>
            </Wrapper >
        );
    },
};

export const PricingWrapper: React.FC<{
    children: React.ReactNode,
    showText: Boolean,
    showIcon: Boolean
}> = ({ children, showText, showIcon }) => {
    return <div className={`${showText ? "tw-px-5 tw-py-2 tw-w-full" : "tw-px-2 tw-py-1 tw-w-fit"} tw-border-transparent tw-rounded tw-shadow-md tw-flex tw-items-center tw-gap-4 tw-bg-background dark:tw-bg-backgroundDark tw-transition-all tw-duration-300 tw-ease-in-out`}>
        {showIcon &&
            (
                showText ?
                    <span>
                        <BsTrophyFill size={22} />
                    </span>
                    :
                    <Tooltip title="Pricing">
                        <span>
                            <BsTrophyFill size={18} />
                        </span>
                    </Tooltip>
            )
        }
        {showText &&
            <div className="text-sm">Pricing</div>
        }
    </div>;
};